import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  createdAt: string
  emailVerified: boolean
  isPremium: boolean
  apiCredits?: {
    creditsUsed: number
    totalFreeCredits: number
    purchasedCredits: number
    totalCreditsRemaining: number
  }
  exportedHealthProfile?: {
    id: string
    profile: string
    source: string
    exportedAt: string
  }
  profile?: {
    name?: string
    biologicalSex?: string
    dateOfBirth?: string
    raceEthnicity?: string
    heightCm?: number
    weightKg?: number
    bmi?: number
  }
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<boolean>
  register: (userData: RegisterData) => Promise<boolean>
  logout: () => void
  updateProfile: (profileData: Partial<User>) => Promise<void>
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Configure axios defaults
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001/api'
axios.defaults.baseURL = API_BASE_URL

// Add request interceptor to include auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't automatically redirect on 401 errors - let components handle them
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      // window.location.href = '/login' // Commented out to prevent page reload
    }
    return Promise.reject(error)
  }
)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      // Verify token and get user info
      verifyToken()
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async () => {
    try {
      const response = await axios.get('/auth/me')
      setUser(response.data.user)
    } catch (error) {
      console.error('Token verification failed:', error)
      localStorage.removeItem('token')
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post('/auth/login', { email, password })
      
      // Access token and user directly from response.data
      const token = response.data.token
      const userData = response.data.user

      localStorage.setItem('token', token)
      setUser(userData)
      
      return true
    } catch (error: any) {
      // Re-throw the error so the Login component can catch it
      throw error
    }
  }

  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      await axios.post('/auth/register', userData)
      
      toast.success('Registration successful! Please check your email for verification.')
      return true
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed'
      toast.error(message)
      return false
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
    toast.success('Logged out successfully')
  }

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      const response = await axios.put('/users/profile', profileData)
      setUser(response.data.user)
      toast.success('Profile updated successfully')
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update profile'
      toast.error(message)
      throw error
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
